import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { concatMap, finalize, forkJoin, from } from 'rxjs';
import { MaterialModule } from '../material.module';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { CalibrationFrequency, CalibrationTemplateItem, LabEquipmentInstrumentType } from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

@Component({
  selector: 'app-instrument-type-matrix',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, RouterModule],
  templateUrl: './instrument-type-matrix.component.html',
  styleUrl: './instrument-type-matrix.component.scss',
})
export class InstrumentTypeMatrixComponent implements OnInit {
  private readonly service = inject(LabEquipmentService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly frequencyOrder: CalibrationFrequency[] = ['Weekly', 'Monthly', 'Quarterly', 'Annual'];
  types: LabEquipmentInstrumentType[] = [];
  items: CalibrationTemplateItem[] = [];
  draft: Record<number, number[]> = {};
  newTypeName = '';
  editingTypeId: number | null = null;
  editingName = '';
  isLoading = true;
  savingTypeId: number | null = null;
  hoveredCell: { typeId: number; itemId: number; frequency: CalibrationFrequency } | null = null;
  errorMessage = '';

  ngOnInit(): void { this.load(); }

  load(): void {
    this.isLoading = true;
    forkJoin({ types: this.service.getInstrumentTypes(), items: this.service.getCalibrationTemplateItems() })
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: ({ types, items }) => {
          this.types = types;
          this.items = items.sort((left, right) =>
            this.frequencyOrder.indexOf(left.frequency) - this.frequencyOrder.indexOf(right.frequency)
            || left.name.localeCompare(right.name));
          this.draft = Object.fromEntries(types.map(type => [type.id, [...type.calibrationTemplateItemIds]]));
          this.errorMessage = '';
        },
        error: () => this.errorMessage = 'Equipment types could not be loaded.',
      });
  }

  selected(typeId: number, itemId: number): boolean {
    return this.draft[typeId]?.includes(itemId) ?? false;
  }

  get itemGroups(): { frequency: CalibrationFrequency; items: CalibrationTemplateItem[] }[] {
    return this.frequencyOrder.map(frequency => ({
      frequency,
      items: this.items.filter(item => item.frequency === frequency),
    })).filter(group => group.items.length > 0);
  }

  toggleItem(typeId: number, itemId: number, checked: boolean): void {
    this.draft[typeId] = checked
      ? [...(this.draft[typeId] ?? []), itemId]
      : (this.draft[typeId] ?? []).filter(id => id !== itemId);
  }

  changed(type: LabEquipmentInstrumentType): boolean {
    const current = new Set(type.calibrationTemplateItemIds);
    const selected = this.draft[type.id] ?? [];
    return selected.length !== current.size || selected.some(id => !current.has(id));
  }

  get hasPendingChanges(): boolean {
    return this.types.some(type => this.changed(type));
  }

  saveMatrix(type: LabEquipmentInstrumentType): void {
    if (!this.changed(type) || this.savingTypeId !== null) return;
    this.confirmAndSave([type]);
  }

  saveAll(): void {
    if (this.savingTypeId !== null) return;
    const changes = this.types.filter(type => this.changed(type));
    if (changes.length) this.confirmAndSave(changes);
  }

  private confirmAndSave(changes: LabEquipmentInstrumentType[]): void {
    const affected = changes.filter(type => type.calibrationTemplateItemIds.some(id => !this.selected(type.id, id)))
      .reduce((count, type) => count + type.instrumentCount, 0);
    if (affected > 0) {
      this.dialog.open(ConfirmDialogComponent, {
        width: '480px',
        maxWidth: 'calc(100vw - 32px)',
        data: {
          title: 'Disable Lab Calibrations',
          message: `This will disable the unchecked calibrations on ${affected} instrument(s). Existing records will remain available.`,
          confirmLabel: 'Save Changes',
          cancelLabel: 'Cancel',
          variant: 'warning',
        },
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) this.persistMatrices(changes);
      });
      return;
    }
    this.persistMatrices(changes);
  }

  addType(): void {
    const name = this.newTypeName.trim();
    if (!name || this.savingTypeId !== null) return;
    this.savingTypeId = 0;
    this.service.createInstrumentType(name).pipe(finalize(() => this.savingTypeId = null)).subscribe({
      next: () => { this.newTypeName = ''; this.load(); },
      error: () => this.errorMessage = 'The equipment type could not be created.',
    });
  }

  startEdit(type: LabEquipmentInstrumentType): void {
    this.editingTypeId = type.id;
    this.editingName = type.name;
  }

  saveType(type: LabEquipmentInstrumentType, isActive = type.isActive): void {
    const name = (this.editingTypeId === type.id ? this.editingName : type.name).trim();
    if (!name || this.savingTypeId !== null) return;
    this.savingTypeId = type.id;
    this.service.updateInstrumentType(type, name, isActive)
      .pipe(finalize(() => this.savingTypeId = null)).subscribe({
        next: updated => {
          this.types = this.types.map(current => current.id === type.id ? updated : current);
          this.editingTypeId = null;
          this.errorMessage = '';
        },
        error: () => this.errorMessage = 'The equipment type could not be updated.',
      });
  }

  private persistMatrices(changes: LabEquipmentInstrumentType[]): void {
    const selections = changes.map(type => ({ id: type.id, itemIds: [...(this.draft[type.id] ?? [])] }));
    this.savingTypeId = selections[0].id;
    from(selections).pipe(
      concatMap(selection => this.service.updateTypeCalibrations(selection.id, selection.itemIds)),
      finalize(() => this.savingTypeId = null),
    ).subscribe({
        next: updated => {
          this.types = this.types.map(current => current.id === updated.id ? updated : current);
          this.errorMessage = '';
        },
        error: () => this.errorMessage = 'Some Lab calibration changes could not be saved. Review the remaining unsaved rows.',
        complete: () => this.snackBar.open('Lab calibration changes saved.', 'Close', { duration: 5000 }),
      });
  }
}